import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'

import type {PanelArtifact} from './ArtifactPanelContent'
import type {EditableArtifact} from './create-artifact-dialog-types'

export type ArtifactTabItem = {id: string; label: string}
export type EditingArtifact = {
  artifact: EditableArtifact
  returnFocusRef: RefObject<HTMLElement | null>
}

/**
 * Encapsulates the artifacts side-panel tab state: which artifacts are open,
 * which one is active, and the handlers that mutate that set. Keeping it in a
 * hook lets `ArtifactsSection` stay a thin shell of section-level UI state.
 */
export function useArtifactsPanelState() {
  const [openArtifacts, setOpenArtifacts] = useState<PanelArtifact[]>([])
  const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null)
  const [editingArtifact, setEditingArtifact] = useState<EditingArtifact | null>(null)
  // Captures the row that opened the panel so close can return focus there.
  const lastRowTriggerRef = useRef<HTMLElement | null>(null)

  const selectArtifact = useCallback((artifact: PanelArtifact) => {
    lastRowTriggerRef.current = (document.activeElement as HTMLElement | null) ?? null
    setOpenArtifacts(prev => {
      if (prev.some(a => a.id === artifact.id)) {
        return prev.map(a => (a.id === artifact.id ? artifact : a))
      }
      return [...prev, artifact]
    })
    setActiveArtifactId(artifact.id)
  }, [])

  const activate = useCallback((id: string) => {
    setActiveArtifactId(id)
  }, [])

  const reconcileArtifacts = useCallback((artifacts: readonly PanelArtifact[]) => {
    setOpenArtifacts(prev => {
      if (prev.length === 0 || artifacts.length === 0) return prev
      const artifactsById = new Map(artifacts.map(artifact => [artifact.id, artifact]))
      let changed = false
      const next = prev.map(a => {
        const artifact = artifactsById.get(a.id)
        if (!artifact) return a
        const merged = {...a, ...artifact}
        if (panelArtifactsEqual(a, merged)) return a
        changed = true
        return merged
      })
      return changed ? next : prev
    })
  }, [])

  const updateArtifact = useCallback(
    (artifact: PanelArtifact) => {
      reconcileArtifacts([artifact])
    },
    [reconcileArtifacts],
  )

  const editArtifact = useCallback((artifact: EditableArtifact, returnFocusRef: RefObject<HTMLElement | null>) => {
    setEditingArtifact({artifact, returnFocusRef})
  }, [])

  const closeEditArtifact = useCallback(() => {
    setEditingArtifact(null)
  }, [])

  const closePanel = useCallback(() => {
    setActiveArtifactId(null)
  }, [])

  const promoteOverflow = useCallback((id: string) => {
    // Just activating is enough: the tab bar's overflow logic will swap the
    // active tab into the last visible slot on the next render.
    setActiveArtifactId(id)
  }, [])

  const closeTab = useCallback(
    (id: string) => {
      setOpenArtifacts(prev => {
        const idx = prev.findIndex(a => a.id === id)
        if (idx === -1) return prev
        return [...prev.slice(0, idx), ...prev.slice(idx + 1)]
      })
      setActiveArtifactId(current => {
        if (current !== id) return current
        const idx = openArtifacts.findIndex(a => a.id === id)
        const remaining = [...openArtifacts.slice(0, idx), ...openArtifacts.slice(idx + 1)]
        const neighbour = remaining[idx - 1] ?? remaining[idx] ?? null
        return neighbour ? neighbour.id : null
      })
    },
    [openArtifacts],
  )

  const removeArtifact = useCallback(
    (id: string) => {
      if (activeArtifactId === id) {
        lastRowTriggerRef.current = null
      }
      closeTab(id)
    },
    [activeArtifactId, closeTab],
  )

  const activeArtifact = useMemo(
    () => openArtifacts.find(a => a.id === activeArtifactId) ?? null,
    [openArtifacts, activeArtifactId],
  )
  const tabs = useMemo<ArtifactTabItem[]>(() => openArtifacts.map(a => ({id: a.id, label: a.name})), [openArtifacts])

  return {
    activeArtifactId,
    activeArtifact,
    editingArtifact,
    tabs,
    lastRowTriggerRef,
    selectArtifact,
    activate,
    updateArtifact,
    reconcileArtifacts,
    editArtifact,
    closeEditArtifact,
    closePanel,
    promoteOverflow,
    closeTab,
    removeArtifact,
  }
}

function panelArtifactsEqual(a: PanelArtifact, b: PanelArtifact) {
  return (
    a.id === b.id &&
    a.type === b.type &&
    a.name === b.name &&
    a.body === b.body &&
    a.bodyHTML === b.bodyHTML &&
    a.createdAt === b.createdAt &&
    a.updatedAt === b.updatedAt &&
    a.hasEdits === b.hasEdits &&
    a.updatedByActor?.login === b.updatedByActor?.login &&
    a.resourceType === b.resourceType &&
    a.resourceId === b.resourceId &&
    a.number === b.number
  )
}
